import '../../../src/styles/globals.scss';
import './Receipt.scss';
import React, {useEffect, useMemo, useRef, useState} from "react";
import PulPalLoader from "../PulPalLoader/PulPalLoader";
import C2CService from "../../services/c2c";
import {NavLink, useParams, useSearchParams} from "react-router-dom";
import {t} from "i18next";

// PSP (Millikart) C2C receipt DTO — /payment-service/api/c2c/receipt
type C2cReceipt = {
    status: 'Paid' | 'Pending' | 'Failed' | 'Canceled' | 'Expired';
    amount: number;
    currency: string;
    surcharge?: number | null;      // acquirer fee (min 1 AZN / 1%), charged on top
    totalAmount?: number | null;    // amount + surcharge = what the sender's card pays
    maskedCard?: string;            // sender card, already masked server-side
    senderName?: string;
    recipientMaskedCard?: string;   // recipient card, already masked server-side
    recipientName?: string;
    rrn?: string;
    approvalCode?: string;
    purposeName?: string;
    purposeNote?: string;
    reference?: string;
    date?: string;
};

// Every receipt fetch re-checks the transfer against Millikart, but the MillikartConnector keeps a
// successful web transfer Pending for FinalStatusDelayMinutes (5 min, counted from 3DS + exec-tran
// completion); partner (Api-Key) transfers are exempt and come back final on the first fetch.
// So: poll quickly for the first half minute (a non-3DS card or a decline can be final within
// seconds), then slowly for up to ~15 minutes, then give up on polling and show the static
// "in progress" receipt with the refresh button.
const PENDING_POLL_MS = 3000;
const PENDING_POLL_MAX = 10;
const SLOW_POLL_MS = 20000;
const SLOW_POLL_MAX = 45;

// 'pending' = spinner, still polling; 'processing' = we stopped polling and the transfer is still
// not final — static yellow receipt with a refresh button.
type ReceiptStatus = 'success' | 'fail' | 'pending' | 'processing' | 'notfound';

const toFinalStatus = (s?: string): ReceiptStatus =>
    s === 'Paid' ? 'success' : s === 'Pending' ? 'processing' : 'fail';

export const Receipt = () => {
    const [loadingNotTransparent, setLoadingNotTransparent] = useState<boolean>(true);
    const [data, setData] = useState<C2cReceipt | null>(null);
    const [legacyData, setLegacyData] = useState<any>(null);
    const [status, setStatus] = useState<ReceiptStatus>('pending');
    const {id} = useParams();
    const [searchParams] = useSearchParams();
    const pollCount = useRef(0);
    const slowPollCount = useRef(0);

    // New PSP flow redirects to /receipt?transaction={paymentAttempt}&ref={reference};
    // paymentAttempt is what the receipt API expects, so `transaction` wins over `ref`.
    const paymentAttempt = searchParams.get('transaction') ?? searchParams.get('ref');

    useEffect(() => {
        let cancelled = false;
        let timer: ReturnType<typeof setTimeout>;

        const fetchNew = async () => {
            const res: any = await C2CService.c2cReceipt(paymentAttempt as string);
            if (cancelled) return;

            const d: C2cReceipt | undefined = res?.data;
            if (!d) {
                setStatus('notfound');
                setLoadingNotTransparent(false);
                return;
            }

            setData(d);
            setLoadingNotTransparent(false);

            if (d.status === 'Pending') {
                if (pollCount.current < PENDING_POLL_MAX) {
                    pollCount.current += 1;
                    setStatus('pending');
                    timer = setTimeout(fetchNew, PENDING_POLL_MS);
                    return;
                }
                if (slowPollCount.current < SLOW_POLL_MAX) {
                    slowPollCount.current += 1;
                    setStatus('pending');
                    timer = setTimeout(fetchNew, SLOW_POLL_MS);
                    return;
                }
            }

            setStatus(toFinalStatus(d.status));
        };

        // Old external P2P flow still lands on /receipt/:id — keep it working.
        const fetchLegacy = async () => {
            const res: any = await C2CService.c2cReceiptTransfer(id);
            if (cancelled) return;

            setLegacyData(res?.data ?? null);
            const isSuccess = res?.data?.status === 0 && res?.data?.rc === 0;
            setStatus(res?.data ? (isSuccess ? 'success' : 'fail') : 'notfound');
            setLoadingNotTransparent(false);
        };

        const run = async () => {
            try {
                if (paymentAttempt) {
                    await fetchNew();
                } else if (id) {
                    await fetchLegacy();
                } else {
                    setStatus('notfound');
                    setLoadingNotTransparent(false);
                }
            } catch (e) {
                console.error("API error:", e);
                if (!cancelled) {
                    setStatus('notfound');
                    setLoadingNotTransparent(false);
                }
            }
        };

        run();
        return () => {
            cancelled = true;
            if (timer) clearTimeout(timer);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, paymentAttempt]);

    const maskCardNumber = (cardNumber?: string): string => {
        if (!cardNumber) return '';
        if (cardNumber.length !== 16 || cardNumber.includes('*')) return cardNumber;
        return cardNumber.slice(0, 6) + '*'.repeat(6) + cardNumber.slice(12);
    };

    const formatDate = (value?: string) => {
        if (!value) return '';
        const date = new Date(value);
        if (isNaN(date.getTime())) return value;
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        const hour = String(date.getHours()).padStart(2, '0');
        const minute = String(date.getMinutes()).padStart(2, '0');
        return `${day}.${month}.${year} ${hour}:${minute}`;
    };

    const formatAmount = (value?: number | null, currency?: string) =>
        value === null || value === undefined ? undefined : `${value.toFixed(2)} ${currency ?? ''}`.trim();

    const title = useMemo(() => {
        switch (status) {
            case 'success':
                return t('c2c_receipt_success');
            case 'pending':
            case 'processing':
                return t('c2c_receipt_processing');
            case 'notfound':
                return t('c2c_receipt_not_found');
            default:
                return t('c2c_receipt_fail');
        }
    }, [status]);

    const Row = ({label, value, highlight}: { label: string; value?: string; highlight?: boolean }) =>
        value ? (
            <div className={`c2c-receipt__row${highlight ? ' c2c-receipt__row--highlight' : ''}`}>
                <dt>{label}</dt>
                <dd>{value}</dd>
            </div>
        ) : null;

    // Hero amount: what the sender's card actually pays (falls back to the transfer amount).
    const heroAmount = data ? (data.totalAmount ?? data.amount) : legacyData?.amount;
    const heroCurrency = data?.currency ?? legacyData?.currency ?? 'AZN';

    return (
        <div className="c2c-receipt">
            <div className="c2c-receipt__inner">
                <div className={`c2c-receipt__ticket${status === 'pending' || status === 'processing' ? ' c2c-receipt__ticket--processing' : ''}`}>
                    <div className="c2c-receipt__status">
                        <div className={`c2c-receipt__icon c2c-receipt__icon--${status === 'notfound' ? 'fail' : status}`}>
                            {status === 'success' && '✓'}
                            {(status === 'fail' || status === 'notfound') && '✕'}
                            {status === 'pending' && <div className="c2c-receipt__spinner"/>}
                            {status === 'processing' && <div className="c2c-receipt__clock"/>}
                        </div>
                        <h1 className="c2c-receipt__title">{title}</h1>
                        {(paymentAttempt || legacyData?.orderId) && (
                            <div className="c2c-receipt__subtitle">
                                {t('c2c_receipt_number')}: {paymentAttempt ?? legacyData?.orderId}
                            </div>
                        )}
                        {status === 'processing' && (
                            <div className="c2c-receipt__notice">{t('c2c_receipt_processing_hint')}</div>
                        )}
                    </div>

                    {heroAmount !== undefined && heroAmount !== null && (
                        <div className="c2c-receipt__total">
                            <div className="c2c-receipt__total-value">
                                {Number(heroAmount).toFixed(2)}<span>{heroCurrency}</span>
                            </div>
                            <div className="c2c-receipt__total-label">{t('c2c_receipt_total')}</div>
                        </div>
                    )}

                    <hr className="c2c-receipt__divider"/>

                    {data && (
                        <dl>
                            <Row label={t('c2c_receipt_date')} value={formatDate(data.date)}/>
                            <Row label={t('c2c_receipt_service')} value={t('c2c_receipt_service_value') as string}/>
                            <Row label={t('c2c_receipt_purpose')} value={data.purposeName}/>
                            <Row label={t('c2c_receipt_sender_card')} value={data.maskedCard}/>
                            <Row label={t('c2c_receipt_sender')} value={data.senderName}/>
                            <Row label={t('c2c_receipt_recipient_card')} value={data.recipientMaskedCard}/>
                            <Row label={t('c2c_receipt_recipient')} value={data.recipientName}/>
                            <Row label="RRN" value={data.rrn}/>
                            <Row label={t('c2c_receipt_approval')} value={data.approvalCode}/>
                            <hr className="c2c-receipt__divider"/>
                            <Row label={t('c2c_receipt_amount')} value={formatAmount(data.amount, data.currency)}/>
                            <Row label={t('Commission')} value={formatAmount(data.surcharge, data.currency)}/>
                            <Row label={t('c2c_receipt_total')} highlight
                                 value={formatAmount(data.totalAmount, data.currency)}/>
                        </dl>
                    )}

                    {legacyData && (
                        <dl>
                            <Row label={t('c2c_receipt_date')} value={formatDate(legacyData.createdAt)}/>
                            <Row label={t('c2c_receipt_service')} value={t('c2c_receipt_service_value') as string}/>
                            <Row label={t('c2c_receipt_sender_card')}
                                 value={maskCardNumber(legacyData.beneficiarCard?.cardNumber)}/>
                            <Row label={t('c2c_receipt_recipient_card')}
                                 value={maskCardNumber(legacyData.receiverCard)}/>
                            <hr className="c2c-receipt__divider"/>
                            <Row label={t('c2c_receipt_amount')}
                                 value={legacyData.amount != null ? `${legacyData.amount} ${legacyData.currency ?? ''}`.trim() : undefined}/>
                            <Row label={t('Commission')}
                                 value={legacyData.fee != null ? `${legacyData.fee} ${legacyData.currency ?? ''}`.trim() : undefined}/>
                        </dl>
                    )}
                </div>

                <div className="c2c-receipt__actions">
                    {status === 'processing' && (
                        <button type="button" className="c2c-receipt__btn c2c-receipt__btn--ghost"
                                onClick={() => window.location.reload()}>
                            {t('c2c_receipt_refresh')}
                        </button>
                    )}
                    <NavLink className="c2c-receipt__btn c2c-receipt__btn--primary" to='/'>
                        {t('c2c_receipt_home')}
                    </NavLink>
                </div>
            </div>

            {loadingNotTransparent && (
                <div
                    className={`pulpal-loader-wrapper ${loadingNotTransparent ? 'pulpal-loader-wrapper--not-transparent' : ''} `}>
                    <PulPalLoader/>
                </div>
            )}
        </div>
    )
}
